import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** How long the track stays still after the customer stops interacting. */
const RESUME_DELAY = 2000;

/** Drag distance (px) before we treat a pointer press as a drag. */
const DRAG_THRESHOLD = 3;

/**
 * A review track that drifts sideways on its own and can also be dragged by hand.
 *
 * It is a real scroll container rather than an animated transform, which is what
 * lets touch swipe, trackpad and wheel work natively. The theme's marquee uses
 * `overflow: hidden` + a CSS keyframe, so it cannot be grabbed.
 *
 * The drift only runs when the cards actually overflow. With few enough cards to
 * fit, the track stays a plain static row.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling element holding the cards.
 *
 * @extends Component<Refs>
 */
class ProductReviewsCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {number | null} */
  #frame = null;

  /** @type {number | null} */
  #resumeTimer = null;

  /** Width of one full set of cards, i.e. the point the scroll wraps at. */
  #loopWidth = 0;

  /** Scroll offset kept as a float, since scrollLeft may round. */
  #position = 0;

  /** True while the customer is hovering, dragging or scrolling. */
  #held = false;

  /** True once the cards have been duplicated to make the loop seamless. */
  #looping = false;

  #lastFrameTime = 0;

  /** @type {{ pointerId: number, startX: number, startScroll: number, moved: boolean } | null} */
  #drag = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(() => this.#measure());
    this.#resizeObserver.observe(this.refs.track);

    // Below-the-fold reviews should not clone cards, force layout or animate
    // while the product's first screen and analytics are still loading.
    this.#visibilityObserver = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.#measure();
      else this.#stop();
    });
    this.#visibilityObserver.observe(this);

    this.addEventListener('pointerenter', this.#hold);
    this.addEventListener('pointerleave', this.#release);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);

    // Wheel and touch scroll the container natively; we only need to know they
    // happened so the drift gets out of the way.
    this.refs.track.addEventListener('wheel', this.#nudge, { passive: true });
    this.refs.track.addEventListener('touchstart', this.#hold, { passive: true });
    this.refs.track.addEventListener('touchend', this.#release, { passive: true });

    // Keyboard focus inside the track means someone is reading it.
    this.addEventListener('focusin', this.#hold);
    this.addEventListener('focusout', this.#release);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#clearResumeTimer();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#visible = false;

    this.removeEventListener('pointerenter', this.#hold);
    this.removeEventListener('pointerleave', this.#release);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.refs.track.removeEventListener('wheel', this.#nudge);
    this.refs.track.removeEventListener('touchstart', this.#hold);
    this.refs.track.removeEventListener('touchend', this.#release);
    this.removeEventListener('focusin', this.#hold);
    this.removeEventListener('focusout', this.#release);
  }

  /** Pixels per second the track drifts. Zero means "scrollable, but never moves on its own". */
  get #speed() {
    const value = Number(this.dataset.speed);
    return Number.isFinite(value) && value >= 0 ? value : 24;
  }

  /**
   * Decides whether this instance should scroll at all, and sets up (or tears
   * down) the duplicated cards accordingly. Runs on connect and on resize.
   */
  #measure = () => {
    if (!this.#visible) return;
    const { track } = this.refs;

    // Compare against a single set of cards, otherwise the duplicates we added
    // would make an already-fitting track look like it overflows.
    const contentWidth = this.#looping ? track.scrollWidth / 2 : track.scrollWidth;
    const overflows = contentWidth - track.clientWidth > 1;

    if (!overflows) {
      this.#stop();
      this.#removeClones();
      this.toggleAttribute('data-scrollable', false);
      return;
    }

    this.toggleAttribute('data-scrollable', true);

    // Still draggable, just never drifts: duplicating here would only let the
    // customer scroll into a second copy of the same reviews.
    if (prefersReducedMotion() || this.#speed === 0) {
      this.#stop();
      this.#removeClones();
      return;
    }

    this.#addClones();
    this.#loopWidth = track.scrollWidth / 2;
    this.#start();
  };

  /**
   * Duplicates the cards once so scrolling past the end lands back at the start
   * with no visible seam. Clones are hidden from assistive tech and from tab
   * order, because they are the same reviews over again.
   */
  #addClones() {
    if (this.#looping) return;

    const { track } = this.refs;
    const originals = Array.from(track.children);

    for (const node of originals) {
      const clone = /** @type {HTMLElement} */ (node.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-review-clone', '');
      // @ts-expect-error - inert is not in older lib.dom typings
      clone.inert = true;
      track.appendChild(clone);
    }

    this.#looping = true;
  }

  #removeClones() {
    if (!this.#looping) return;

    for (const clone of this.refs.track.querySelectorAll('[data-review-clone]')) {
      clone.remove();
    }

    this.#looping = false;
    this.#loopWidth = 0;
  }

  #start() {
    if (this.#frame != null) return;

    this.#lastFrameTime = performance.now();
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#frame == null) return;

    cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  #tick = (/** @type {number} */ now) => {
    const elapsed = now - this.#lastFrameTime;
    this.#lastFrameTime = now;

    const { track } = this.refs;

    if (this.#held) {
      // The customer is driving; follow wherever they left the track so the
      // drift picks up from there rather than snapping back.
      this.#position = track.scrollLeft;
    } else if (this.#loopWidth > 0 && this.#speed > 0) {
      // Position is tracked as a float of our own. Writing sub-pixel deltas
      // straight onto scrollLeft loses them whenever the browser rounds, which
      // at these speeds means never moving at all.
      // Elapsed is clamped so a backgrounded tab doesn't resume with one jump.
      const step = (this.#speed * Math.min(elapsed, 100)) / 1000;
      this.#position = this.#normalize(this.#position + step);
      track.scrollLeft = this.#position;
    }

    this.#frame = requestAnimationFrame(this.#tick);
  };

  /**
   * Folds a position into the first copy of the cards. The second copy is
   * identical, so landing back at the equivalent offset is invisible.
   *
   * @param {number} value
   * @returns {number}
   */
  #normalize(value) {
    const loop = this.#loopWidth;
    if (loop <= 0) return value;

    return ((value % loop) + loop) % loop;
  }

  #hold = () => {
    this.#held = true;
    this.#clearResumeTimer();
  };

  #release = () => {
    this.#clearResumeTimer();
    this.#resumeTimer = window.setTimeout(() => {
      this.#held = false;
      this.#resumeTimer = null;
    }, RESUME_DELAY);
  };

  /** A wheel tick is momentary: hold, then start the countdown straight away. */
  #nudge = () => {
    this.#hold();
    this.#release();
  };

  #clearResumeTimer() {
    if (this.#resumeTimer == null) return;

    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = null;
  }

  #onPointerDown = (/** @type {PointerEvent} */ event) => {
    if (!this.hasAttribute('data-scrollable') || event.button !== 0) return;

    this.#hold();
    this.#drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScroll: this.refs.track.scrollLeft,
      moved: false,
    };
  };

  #onPointerMove = (/** @type {PointerEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const distance = event.clientX - drag.startX;

    if (!drag.moved) {
      if (Math.abs(distance) < DRAG_THRESHOLD) return;

      drag.moved = true;
      this.setAttribute('data-dragging', '');
      // Capture only once it is really a drag, so a plain click still lands.
      this.setPointerCapture(event.pointerId);
    }

    // Normalising here is what lets the customer keep dragging backwards past
    // the start, because the browser would otherwise clamp scrollLeft at 0.
    const target = this.#normalize(drag.startScroll - distance);
    this.refs.track.scrollLeft = target;
    this.#position = target;
  };

  #onPointerUp = (/** @type {PointerEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (drag.moved) {
      this.removeAttribute('data-dragging');
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    }

    this.#drag = null;
    this.#release();
  };
}

if (!customElements.get('product-reviews-carousel')) {
  customElements.define('product-reviews-carousel', ProductReviewsCarousel);
}
